import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import config from '../../../config';
import '../../products/form/productForm.scss';   // styles des champs (.form-controle/.form-input)
import './editCommande.css';
import { NdoePage, NdoePageHeader, NdoeFormSection, NdoeActionButton, NdoeError } from '../../../components/ndoe';
import { messageErreur } from '../../../components/ndoe/chargement';

const EditCommande = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [data, setData] = useState({})
  const navigate = useNavigate();
  const [getClient, setGetClient] = useState([]);
  const {pathname} = useLocation();
  const id = pathname.split('/')[2]
  const [isSaving, setIsSaving] = useState(false);
  const [lignes, setLignes] = useState([]);
  const [chargementLignes, setChargementLignes] = useState(true);
  const [erreurLignes, setErreurLignes] = useState(null);
  const [relance, setRelance] = useState(0);

  const handleInputChange = (e) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    let updatedValue = fieldValue;

    if (fieldName === "contact_email") {
      updatedValue = fieldValue.toLowerCase();
    } else if (Number.isNaN(Number(fieldValue))) {
      updatedValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
    }

    setData((prev) => ({ ...prev, [fieldName]: updatedValue }));
  };

  const handleClick = async (e) => {
    e.preventDefault();

    try{
      setIsSaving(true);
      await axios.put(`${DOMAIN}/api/commande/commandePut/${id}`, data)
      Swal.fire({
        title: 'Success',
        text: 'Commande modifiée avec succès!',
        icon: 'success',
        confirmButtonText: 'OK',
      });
      navigate('/listeCommande')   // la liste se recharge à l'arrivée (plus de window.location.reload)
    }catch(err) {
      Swal.fire({
        title: 'Error',
        text: err.message,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }finally{
      setIsSaving(false);
    }
  }

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/client`);
        setGetClient(data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchClient();
  }, [DOMAIN]);

  useEffect(() => {
    const fetchCommande = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/commande/commandeOne/${id}`);
        setData(Array.isArray(data) ? (data[0] || {}) : (data || {}));
      } catch (error) {
        console.log(error);
      }
    };
    fetchCommande();
  }, [DOMAIN, id]);

  // Ce que contient la commande — en lecture. On ne réaffecte pas une commande
  // à l'aveugle : il faut voir ce qu'on déplace.
  useEffect(() => {
    const fetchLignes = async () => {
      setChargementLignes(true);
      setErreurLignes(null);
      try {
        const { data } = await axios.get(`${DOMAIN}/api/commande/detail-commande/${id}`);
        setLignes(Array.isArray(data) ? data : []);
      } catch (error) {
        // UNE PANNE N'EST PAS UNE COMMANDE VIDE (10/08/2026). Sans message, une
        // commande dont les lignes n'ont pas pu être chargées ressemblait à une
        // commande sans articles — on pouvait la modifier en croyant la voir
        // entière.
        setLignes([]);
        setErreurLignes(messageErreur(error, 'les articles de cette commande'));
      } finally {
        setChargementLignes(false);
      }
    };
    fetchLignes();
  }, [DOMAIN, id, relance]);


  const total = lignes.reduce((s, l) => s + (Number(l.prix) || 0) * (Number(l.quantite) || 0), 0);

  return (
    <NdoePage>
      <div className="clientForm">
        <NdoePageHeader
          eyebrow="Commerce"
          title={`Modifier la commande #${id}`}
          subtitle={data?.nom ? `Actuellement au nom de ${data.nom}` : 'Réaffecter la commande à une autre cliente'}
        />

        {/* CE QU'ON MODIFIE, SOUS LES YEUX.
            L'écran ne montrait que le sélecteur de cliente — et vide, parce
            qu'il lisait `id_client` quand l'API n'exposait que `id`. On
            réaffectait donc une commande sans savoir laquelle. */}
        <NdoeFormSection step={1} title="Cette commande" collapsible={false}>
          {chargementLignes && <p className="ec-vide">Chargement des articles…</p>}
          {/* On ne dit « aucun article » que si on a VRAIMENT eu la réponse. */}
          {!chargementLignes && !erreurLignes && !lignes.length && (
            <p className="ec-vide">Cette commande ne contient aucun article.</p>
          )}
          {!chargementLignes && erreurLignes && (
            <NdoeError
              title="Les articles de cette commande n’ont pas pu être chargés"
              text={`${erreurLignes} Ne réaffectez pas cette commande sans avoir vu ce qu’elle contient.`}
              onRetry={() => setRelance((n) => n + 1)}
            />
          )}
          {!!lignes.length && (
            <div className="ec-lignes">
              {lignes.map((l) => (
                <div className="ec-ligne" key={l.id_detail}>
                  <span className="ec-ligne__art">
                    <b>{l.nom_produit || `Article ${l.id_varianteProduit}`}</b>
                    <small>
                      {l.taille ? `P${l.taille}` : ''}
                      {l.code_article ? ` · ${l.code_article}` : ''}
                      {Number(l.quantite) > 1 ? ` · ×${l.quantite}` : ''}
                    </small>
                  </span>
                  <span className="ec-ligne__prix num">
                    {Number(l.prix) === 0 ? (
                      <>
                        <span className="ec-offerte">Offerte</span>
                        {l.motif_gratuite && <small>{l.motif_gratuite}</small>}
                      </>
                    ) : `${(Number(l.prix) || 0).toLocaleString('fr-FR')} $`}
                  </span>
                </div>
              ))}
              <div className="ec-total">
                <span>{lignes.length} article(s)</span>
                <b className="num">{total.toLocaleString('fr-FR')} $</b>
              </div>
            </div>
          )}
        </NdoeFormSection>

        <NdoeFormSection step={2} title="Cliente" collapsible={false}>
          <div className="form-controle">
            <label>Client</label>
            {/* `id_client` vient désormais de l'API (avant : seulement `id`,
                d'où un sélecteur toujours vide). Le repli sur `data.id` garde
                l'écran fonctionnel si une ancienne API répond encore. */}
            <select name="id_client" id="id_client" value={data?.id_client ?? data?.id ?? ''} className="form-input" onChange={handleInputChange} required>
              <option value="">Sélectionnez un client</option>
              {getClient.map((dd) => (
                <option key={dd.id} value={dd.id}>{dd.nom}</option>
              ))}
            </select>
          </div>
        </NdoeFormSection>

        <div className="ndoe-sticky-cta">
          <NdoeActionButton variant="secondary" onClick={() => navigate('/listeCommande')}>Annuler</NdoeActionButton>
          <NdoeActionButton variant="primary" onClick={handleClick} loading={isSaving}>Modifier</NdoeActionButton>
        </div>
      </div>
    </NdoePage>
  )
}

export default EditCommande
